import React from 'react';
import PropTypes from 'prop-types';

import { Form, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faExclamationTriangle, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { auroMetalSaurus, darkOrange } from 'lib/css/colors';
import { BLANK_WARNING_HINT } from 'components/manage_brands/brand_verify/constants';
import FieldLabel from 'components/manage_brands/brand_verify/FieldLabel';
import ManufacturerFilter from 'components/manage_brands/brand_details/ManufacturerFilter';
import sharedStyles from 'components/manage_brands/brand_verify/sharedStyles';

function OverwriteWarning({ message }) {
  return (
    <div className={css(styles.overwriteWarning)}>
      <FontAwesomeIcon icon={faExclamationTriangle} className={css(styles.overwriteWarningIcon)} />
      {message}
    </div>
  );
}

OverwriteWarning.propTypes = {
  message: PropTypes.string.isRequired,
};

function ExistingManufacturerFields({
  manufacturerId,
  name,
  url,
  savedName,
  savedUrl,
  loading,
  filterKey,
  onPickManufacturer,
  onChangeName,
  onChangeUrl,
  onFieldFocus,
  warningVerb,
  disabledByDefault,
  nameBlankWarning,
  urlBlankWarning,
}) {
  const fieldsDisabled = loading || disabledByDefault;
  const showNameWarning = manufacturerId != null && savedName !== '' && name !== savedName;
  const showUrlWarning = manufacturerId != null && savedUrl !== '' && url !== savedUrl;

  const handleFocus = () => {
    if (onFieldFocus) onFieldFocus();
  };

  return (
    <>
      <div className="mb-2">
        <FieldLabel label="Pick existing manufacturer" />
        <ManufacturerFilter
          key={filterKey}
          manufacturerID={manufacturerId}
          onChange={onPickManufacturer}
          autoFocus={false}
        />
      </div>
      <div className="mb-2">
        <FieldLabel
          label="Manufacturer Name"
          warning={nameBlankWarning ? BLANK_WARNING_HINT : null}
        />
        <Form.Control
          type="text"
          value={name}
          onChange={e => onChangeName(e.target.value)}
          onFocus={handleFocus}
          disabled={fieldsDisabled}
          className={css(sharedStyles.textInput)}
          autoCorrect="off"
        />
        {showNameWarning && (
          <OverwriteWarning message={`Will overwrite saved name on ${warningVerb}`} />
        )}
      </div>
      <div>
        <div className={css(sharedStyles.urlLabelRow)}>
          <FieldLabel
            label="Manufacturer URL"
            warning={urlBlankWarning ? BLANK_WARNING_HINT : null}
          />
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              className={css(sharedStyles.urlOpenIcon)}
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} />
            </a>
          )}
          {loading && (
            <Spinner
              animation="border"
              size="sm"
              role="status"
              className={css(styles.urlLoadingSpinner)}
            />
          )}
        </div>
        <Form.Control
          type="text"
          value={url}
          onChange={e => onChangeUrl(e.target.value)}
          onFocus={handleFocus}
          disabled={fieldsDisabled}
          className={css(sharedStyles.textInput)}
          autoCorrect="off"
        />
        {showUrlWarning && (
          <OverwriteWarning message={`Will overwrite saved URL on ${warningVerb}`} />
        )}
      </div>
    </>
  );
}

ExistingManufacturerFields.propTypes = {
  manufacturerId: PropTypes.number,
  name: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  savedName: PropTypes.string.isRequired,
  savedUrl: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  filterKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onPickManufacturer: PropTypes.func.isRequired,
  onChangeName: PropTypes.func.isRequired,
  onChangeUrl: PropTypes.func.isRequired,
  onFieldFocus: PropTypes.func,
  warningVerb: PropTypes.string,
  disabledByDefault: PropTypes.bool,
  nameBlankWarning: PropTypes.bool,
  urlBlankWarning: PropTypes.bool,
};

ExistingManufacturerFields.defaultProps = {
  loading: false,
  warningVerb: 'verify',
  disabledByDefault: false,
  nameBlankWarning: false,
  urlBlankWarning: false,
};

const styles = StyleSheet.create({
  urlLoadingSpinner: {
    marginLeft: 8,
    width: '0.85rem',
    height: '0.85rem',
    borderWidth: '0.15em',
    color: auroMetalSaurus,
  },
  overwriteWarning: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    color: darkOrange,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  overwriteWarningIcon: {
    fontSize: '0.75rem',
  },
});

export default ExistingManufacturerFields;
